/**
 * FieldMediaViewer (Inventory Screens Step 5.3 Phase 2) — full-screen photo
 * viewer for the field modal. Ports the FEEL of Bob's Stage-6 viewer
 * (reference/toolsight-inv-v11.07.html :11582-11674), not its data model:
 *
 *   - swipe left/right navigates when unzoomed (dx > 50, |dy| < 70)
 *   - dots indicator for multi-photo records
 *   - pinch-zoom to 5x + one-finger pan while zoomed (Bob's transform
 *     formula: scale(s) translate(x/s, y/s)); zoom resets on nav/open
 *   - back-arrow closes; Delete → confirm overlay → onDelete
 *
 * Nav: the HOST owns the sentinel (tmgFieldMediaViewerIdx, mirroring the
 * Phase 1 sheet's tmgFieldSheetField pattern) — isOpen derives from
 * location.state and every close path funnels through onClose →
 * navigate(-1). Escape is capture-phase + stopPropagation (the Phase 1
 * precedent) so ModalShell's bubble listener never double-closes. The
 * delete CONFIRM overlay is local state, not a sentinel — hardware-back
 * with the confirm open closes the viewer (documented deviation from
 * Bob's every-layer-pushState model; one sentinel per surface).
 *
 * Touch listeners are attached natively (not React synthetic) because
 * touchmove must be non-passive to preventDefault pinch/pan scrolling.
 * Zoom/pan state lives in refs and applies imperatively to the <img> —
 * re-rendering per touchmove frame would be jank.
 *
 * Index is LOCAL state seeded from initialIndex — swiping must not push
 * history. The media array can shrink under us (delete): index clamps,
 * and an emptied array closes the viewer via the host.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { mediaThumbSrc } from './FieldMediaGallery';
import './FieldMediaViewer.css';

const SWIPE_DX = 50;
const SWIPE_DY = 70;
const MAX_SCALE = 5;

export default function FieldMediaViewer({ isOpen, media = [], initialIndex = 0, onClose, onDelete }) {
  const [idx, setIdx] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const stageRef = useRef(null);
  const imgRef = useRef(null);
  const scaleRef = useRef(1);
  const panRef = useRef({ x: 0, y: 0 });
  const pinchRef = useRef({ dist0: 0, scale0: 1 });
  const touchRef = useRef({ x0: 0, y0: 0, panX0: 0, panY0: 0, count: 0, active: false });

  const safeIdx = media.length > 0 ? Math.min(idx, media.length - 1) : 0;
  const idxRef = useRef(0);
  idxRef.current = safeIdx;
  const lenRef = useRef(0);
  lenRef.current = media.length;

  const applyTransform = useCallback(() => {
    const img = imgRef.current;
    if (!img) return;
    const s = scaleRef.current;
    const { x, y } = panRef.current;
    img.style.transform = s > 1 ? `scale(${s}) translate(${x / s}px, ${y / s}px)` : '';
  }, []);

  const resetZoom = useCallback(() => {
    scaleRef.current = 1;
    panRef.current = { x: 0, y: 0 };
    applyTransform();
  }, [applyTransform]);

  const goTo = useCallback((next) => {
    setIdx(next);
    resetZoom();
  }, [resetZoom]);

  // Seed index + clear zoom/confirm on every open.
  useEffect(() => {
    if (isOpen) {
      setIdx(initialIndex ?? 0);
      setConfirmOpen(false);
      resetZoom();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Escape — capture + stopPropagation (Phase 1 sheet precedent): one press,
  // one layer. Confirm-open Escape just dismisses the confirm.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      setConfirmOpen((wasOpen) => {
        if (!wasOpen) onClose();
        return false;
      });
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [isOpen, onClose]);

  // Native touch listeners — pinch-zoom + pan + swipe (Bob's gesture model).
  useEffect(() => {
    if (!isOpen) return undefined;
    const el = stageRef.current;
    if (!el) return undefined;

    const onTouchStart = (e) => {
      touchRef.current.count = e.touches.length;
      if (e.touches.length === 2) {
        pinchRef.current = {
          dist0: Math.hypot(
            e.touches[0].clientX - e.touches[1].clientX,
            e.touches[0].clientY - e.touches[1].clientY
          ),
          scale0: scaleRef.current,
        };
      } else if (e.touches.length === 1) {
        touchRef.current = {
          x0: e.touches[0].clientX,
          y0: e.touches[0].clientY,
          panX0: panRef.current.x,
          panY0: panRef.current.y,
          count: 1,
          active: true,
        };
      }
    };

    const onTouchMove = (e) => {
      e.preventDefault();
      if (e.touches.length === 2 && pinchRef.current.dist0 > 0) {
        const d = Math.hypot(
          e.touches[0].clientX - e.touches[1].clientX,
          e.touches[0].clientY - e.touches[1].clientY
        );
        scaleRef.current = Math.min(
          MAX_SCALE,
          Math.max(1, (pinchRef.current.scale0 * d) / pinchRef.current.dist0)
        );
        applyTransform();
      } else if (e.touches.length === 1 && touchRef.current.active && scaleRef.current > 1) {
        panRef.current = {
          x: touchRef.current.panX0 + (e.touches[0].clientX - touchRef.current.x0),
          y: touchRef.current.panY0 + (e.touches[0].clientY - touchRef.current.y0),
        };
        applyTransform();
      }
    };

    const onTouchEnd = (e) => {
      if (touchRef.current.count === 1 && touchRef.current.active && scaleRef.current <= 1) {
        const dx = e.changedTouches[0].clientX - touchRef.current.x0;
        const dy = e.changedTouches[0].clientY - touchRef.current.y0;
        if (Math.abs(dx) > SWIPE_DX && Math.abs(dy) < SWIPE_DY) {
          if (dx < 0 && idxRef.current < lenRef.current - 1) goTo(idxRef.current + 1);
          else if (dx > 0 && idxRef.current > 0) goTo(idxRef.current - 1);
        }
      }
      touchRef.current.count = e.touches.length;
      touchRef.current.active = e.touches.length === 0 ? false : touchRef.current.active;
      if (e.touches.length === 0) pinchRef.current.dist0 = 0;
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    el.addEventListener('touchcancel', onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [isOpen, applyTransform, goTo]);

  if (!isOpen || media.length === 0) return null;

  const entry = media[safeIdx];
  const src = mediaThumbSrc(entry);

  const handleDeleteConfirmed = () => {
    setConfirmOpen(false);
    onDelete(entry, safeIdx);
  };

  return (
    <div className="fmv" role="dialog" aria-modal="true" aria-label="Photo viewer" data-testid="field-media-viewer">
      <div className="fmv-hdr">
        <button type="button" className="fmv-back" onClick={onClose} aria-label="Close viewer" data-testid="fmv-back">
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M15 6l-6 6l6 6" />
          </svg>
        </button>
        <span className="fmv-count" data-testid="fmv-count">
          {safeIdx + 1} / {media.length}
          {entry.pending ? ' · pending sync' : ''}
        </span>
      </div>

      <div className="fmv-stage" ref={stageRef}>
        {src
          ? <img ref={imgRef} className="fmv-img" src={src} alt="" draggable={false} data-testid="fmv-img" />
          : <span className="fmv-missing">Preview unavailable</span>}
        {media.length > 1 && (
          <div className="fmv-dots" data-testid="fmv-dots">
            {media.map((m, i) => (
              <div
                key={m.outbox_id ?? m.media_id ?? i}
                className={`fmv-dot${i === safeIdx ? ' fmv-dot-active' : ''}`}
              />
            ))}
          </div>
        )}
      </div>

      <div className="fmv-foot">
        <button
          type="button"
          className="fmv-delete"
          onClick={() => setConfirmOpen(true)}
          data-testid="fmv-delete"
        >
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M4 7l16 0" />
            <path d="M10 11l0 6" />
            <path d="M14 11l0 6" />
            <path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2l1-12" />
            <path d="M9 7v-3a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v3" />
          </svg>
          Delete
        </button>
      </div>

      {confirmOpen && (
        <div className="fmv-confirm-backdrop" onClick={() => setConfirmOpen(false)}>
          <div className="fmv-confirm-box" onClick={(e) => e.stopPropagation()} data-testid="fmv-confirm">
            <div className="fmv-confirm-title">Delete photo?</div>
            <div className="fmv-confirm-body">
              This photo will be permanently removed from this record.
            </div>
            <div className="fmv-confirm-btns">
              <button type="button" className="fmv-confirm-cancel" onClick={() => setConfirmOpen(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="fmv-confirm-delete"
                onClick={handleDeleteConfirmed}
                data-testid="fmv-confirm-delete"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
